import { CalendarDays, X } from 'lucide-react';
import { Popover } from '../common/Popover';
import { describeDue, quickDates } from './taskUi';

export interface DueDatePickerProps {
  /** YYYY-MM-DD, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  todayKey: string;
  /** The task's event, for "Week before event" / "Event day". */
  eventStartAt?: Date | string | null;
  /** How the trigger reads with no date chosen. */
  placeholder?: string;
  className?: string;
}

/**
 * A due date in one tap: the common choices as chips, a calendar field for
 * anything else, and a way to clear it. Shared by quick add, the task editor
 * and bulk actions so a date is picked the same way everywhere.
 */
export function DueDatePicker({
  value,
  onChange,
  todayKey,
  eventStartAt,
  placeholder = 'Due date',
  className = '',
}: DueDatePickerProps) {
  const current = value ? describeDue(value, todayKey).label : placeholder;
  return (
    <Popover
      ariaLabel="Choose a due date"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={value ? `Due ${current}` : placeholder}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ${className}`}
        >
          <CalendarDays size={14} className="text-gray-400" />
          <span className={value ? '' : 'text-gray-400'}>{current}</span>
        </button>
      )}
      panelClassName="w-64 p-3"
    >
      {(close) => (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {quickDates(todayKey, eventStartAt).map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => {
                  onChange(q.value);
                  close();
                }}
                className={`px-2 py-1 text-xs rounded-full border ${
                  value === q.value
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <label className="block text-xs text-gray-500">
            Pick a date
            <input
              type="date"
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                if (e.target.value) close();
              }}
              className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                close();
              }}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
            >
              <X size={12} /> No due date
            </button>
          )}
        </div>
      )}
    </Popover>
  );
}
